import Maybe from "@mixitone/maybe";
import {
  Account,
  AccountUser,
  Club,
  ClubPermission,
  FeedbackMessage,
  type ClubPlayer,
  type Night,
} from "@mixitone/models";
import { List, Model, oom, subscribeList } from "@mixitone/oom";
import { SubscriptionManager } from "@mixitone/oom/SubscriptionManager";
import { debounce, isNil } from "@mixitone/util";
import { captureException, identifyUser } from "@mixitone/telemetry";
import { openUrl } from "@tauri-apps/plugin-opener";
import { family } from "@tauri-apps/plugin-os";
import { relaunch } from "@tauri-apps/plugin-process";
import actionLogger from "lib/actionLogger";
import { installDevConsole } from "lib/dev/console";
import { isDevMode } from "lib/environment";
import NfcRegistrationService from "lib/nfc/NfcRegistrationService";
import type { User } from "lib/session/SessionAdapter";
import { createCrossSupabase } from "lib/supabaseClient";
import tauri from "lib/tauri";
import { listen as listenMixitoneEvent } from "lib/tauri/MixitoneEvent";
import { messagesService, onlineService } from "services";
import agentToolsService, { resource } from "services/AgentToolsService";
import { registerBrowserTools } from "services/agentTools/browser";
import { registerResourceAnalysisTools } from "services/agentTools/resourceAnalysis";
import {
  MIXITONE_UPDATER_PROGRESS_EVENT,
  isDebugUpdateUiEnabled,
  isUpdaterDisabled,
  shouldCheckForUpdates,
  resolveUpdateChannel,
  type MixitoneUpdaterProgressPayload,
} from "updater/autoUpdate";
import { confirmHotswapReady, tryHotswapFrontendUpdate } from "updater/hotswapUpdate";
import { clubSwitchTargetPathname, resolveClubSwitchNavigation } from "lib/clubSwitchNavigation";
import MixitoneController from "./MixitoneController";
import RouteController from "./RouteController";
import SessionController from "./SessionController";

interface State {
  online: boolean;
  loading: boolean;
  creatingAccount: boolean;
  token: string | null;
  error: string | null;
  accounts: List<Account>;
  clubs: List<Club>;
  currentClub: Club | null;

  updateAvailable?: boolean;
  updateVersion?: string;
  /** Client-side update UX (main window toast). */
  updatePhase: "idle" | "downloading" | "ready" | "installing" | "error";
  updateDownloadProgress: number;
  updateError?: string;
  /** True when the installer is cached locally and can be installed offline. */
  updateCached?: boolean;
  updateDevMock?: boolean;

  showAgent?: boolean;
  /**
   * One-shot text injected into the assistant input on next open. Consumed (cleared) by
   * `AgentController.initialize()` so it never appears twice. Setters should call
   * `actionShowAssistantWithPrefill(text)` rather than touching this directly.
   */
  pendingAssistantPrefill?: string | null;
  /**
   * One-shot text for a new assistant chat when the popup is already open (prefill only;
   * user sends). Consumed by `AgentController.initialize()`.
   */
  pendingAssistantNewChatPrefill?: string | null;
  /**
   * One-shot new chat + send when the assistant popup is not mounted yet (e.g. tour
   * "Continue in assistant"). Consumed by `AgentController.initialize()`.
   */
  pendingAssistantLaunch?: { message: string; additionalContext?: string } | null;
  showFeedback?: boolean;
  newFeedbackMessages?: boolean;
  feedbackMessages: List<FeedbackMessage>;
  feedbackMessagesLoading: boolean;

  headerHeight: number;

  nfcService: NfcRegistrationService | null;

  /** Bumped when Cmd/Ctrl+K fires before GlobalSearchController has registered. */
  pendingGlobalSearchFocus: number;

  /** Desktop app is hidden to tray; skip auto update prompts while backgrounded. */
  appInBackground: boolean;
}

interface PlayerAddedEvent {
  model: Night;
  clubPlayer: ClubPlayer;
}

interface Props {
  ready?: boolean;
}

export interface AppController {
  titles: string[];
}

@actionLogger
export class AppController extends MixitoneController<State, Props> {
  static override initialState: State = {
    online: true,
    loading: false,
    creatingAccount: false,
    token: null,
    error: null,
    accounts: new List(),
    clubs: new List(),
    currentClub: null,
    headerHeight: 0,
    nfcService: null,

    showAgent: false,
    pendingAssistantPrefill: null,
    pendingAssistantNewChatPrefill: null,
    pendingAssistantLaunch: null,
    newFeedbackMessages: false,
    feedbackMessages: new List(),
    feedbackMessagesLoading: true,

    updatePhase: "idle",
    updateDownloadProgress: 0,
    pendingGlobalSearchFocus: 0,
    appInBackground: false,
  };

  titles = this.observable([] as string[]);
  checkForUpdatesInterval?: ReturnType<typeof setInterval>;

  /** Set by AgentController while the assistant popup is mounted. */
  private assistantShortcutHandler: ((text: string, mode: "new-chat" | "prefill") => void) | null = null;

  private assistantLaunchHandler: ((message: string, additionalContext?: string) => void) | null = null;

  private globalSearchFocusHandler: (() => void) | null = null;

  registerAssistantShortcutHandler(handler: ((text: string, mode: "new-chat" | "prefill") => void) | null) {
    this.assistantShortcutHandler = handler;
  }

  registerAssistantLaunchHandler(handler: ((message: string, additionalContext?: string) => void) | null) {
    this.assistantLaunchHandler = handler;
  }

  registerGlobalSearchFocusHandler(handler: (() => void) | null) {
    this.globalSearchFocusHandler = handler;
    if (handler && this.state.pendingGlobalSearchFocus > 0) {
      this.state.pendingGlobalSearchFocus = 0;
      handler();
    }
  }

  actionRequestGlobalSearchFocus() {
    if (this.globalSearchFocusHandler) {
      this.globalSearchFocusHandler();
      return;
    }
    this.state.pendingGlobalSearchFocus += 1;
  }

  consumePendingGlobalSearchFocus(): boolean {
    if (this.state.pendingGlobalSearchFocus <= 0) return false;
    this.state.pendingGlobalSearchFocus = 0;
    return true;
  }

  static isGlobalSearchShortcut(e: KeyboardEvent): boolean {
    if (e.code !== "KeyK" || e.repeat) return false;
    if (!e.metaKey && !e.ctrlKey) return false;
    if (e.shiftKey || e.altKey) return false;

    const el = document.activeElement;
    if (el instanceof HTMLElement) {
      if (el.isContentEditable) return false;
      if (el.closest('[role="dialog"], [role="alertdialog"]')) return false;
      if (el.closest('[data-testid="global-search-root"]')) return false;
    }

    return true;
  }

  private debugFakeTimer?: ReturnType<typeof setInterval>;
  private static readonly OPEN_SETTINGS_EVENT = "mixitone:open_settings" as const;

  override async initialize(_props: Props) {
    const globalSearchShortcut = (e: KeyboardEvent) => {
      if (!AppController.isGlobalSearchShortcut(e)) return;
      e.preventDefault();
      e.stopPropagation();
      this.actionRequestGlobalSearchFocus();
    };
    document.addEventListener("keydown", globalSearchShortcut, true);
    this.addDependency(() => document.removeEventListener("keydown", globalSearchShortcut, true));

    if (tauri.enabled && !import.meta.env.DEV && !isUpdaterDisabled()) {
      await this.actionInstallCachedUpdateOnStartup();
    }

    const loginState = await this.sessionController.waitForSession();
    if (loginState === "signed-out") {
      this.state.loading = true;
      requestAnimationFrame(() => {
        this.actionForward("/login", { state: { from: window.location.pathname } });
      });
      return;
    }

    this.observe(() => {
      this.state.online = onlineService.state.online;
      this.checkForUpdates();
    });

    window.addEventListener("resize", debounce(this.updateHeaderHeight.bind(this), 100), {
      signal: this.signal,
    });

    this.observeTitle();

    // Register before observeLoginState so we do not miss a sync that finishes while
    // account/clubs are still loading. Full sync applies rows with trigger_updates=false,
    // so clubs can land in SQLite without mixitonedb INSERT events.
    if (tauri.enabled) {
      this.addDependency(
        await listenMixitoneEvent("mixitone:sync:finished", () => {
          void this.refreshClubsFromLocalDb();
        }),
      );
    }

    this.observeLoginState();

    if (isDevMode()) {
      installDevConsole(this);
    }

    this.addDependency(
      "playerAdded",
      Model.events.on("night:playerAdded", (event: PlayerAddedEvent) => {
        const { clubPlayer } = event;
        messagesService.addMessage(`hello ${clubPlayer.name}`, "registered", "registered");
      }),
    );

    this.addDependency(
      "playerRetired",
      Model.events.on("night:playerRetired", (event: { player: ClubPlayer }) => {
        const { player } = event;
        messagesService.addMessage(`${player.name} has retired`, "success");
      }),
    );

    if (tauri.enabled) {
      this.state.nfcService = new NfcRegistrationService(this);
      this.addDependency(
        await tauri.listen(AppController.OPEN_SETTINGS_EVENT, () => {
          this.actionOpenSettings();
        }),
      );

      /** Windows omits native app menu; match macOS Cmd+, Settings via Ctrl+, */
      const windowsOpenSettingsShortcut = (e: KeyboardEvent) => {
        if (e.key !== "," || e.repeat) return;
        if (!e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) return;
        try {
          if (family() !== "windows") return;
        } catch {
          return;
        }
        e.preventDefault();
        this.actionOpenSettings();
      };
      window.addEventListener("keydown", windowsOpenSettingsShortcut);
      this.addDependency(() => window.removeEventListener("keydown", windowsOpenSettingsShortcut));

      // Intercept all clicks and forward them through the route controller when
      // tauri is enabled
      const clickHandler = (event: MouseEvent) => {
        if (!(event.target instanceof Element)) return;
        const anchor = event.target.closest("a");
        if (!anchor) return;
        const href = anchor.getAttribute("href");
        if (!href || href.startsWith("#")) return;

        // External URLs and mailto:/tel: must open via the system handler; target=_blank does not
        // reliably open a browser window inside the Tauri webview.
        if (
          /^https?:\/\//i.test(href) ||
          href.startsWith("//") ||
          /^mailto:/i.test(href) ||
          /^tel:/i.test(href)
        ) {
          event.preventDefault();
          void openUrl(href);
          return;
        }

        event.preventDefault();
        this.actionForward(href as any);
      };
      document.addEventListener("click", clickHandler);
      this.addDependency(() => {
        document.removeEventListener("click", clickHandler);
      });

      if (shouldCheckForUpdates()) {
        this.checkForUpdatesInterval = setInterval(
          () => {
            this.checkForUpdates();
          },
          1000 * 60 * 5,
        );
        this.addDependency(() => {
          clearInterval(this.checkForUpdatesInterval!);
        });
      }

      this.addDependency(
        await tauri.listen("app:background", () => {
          this.state.appInBackground = true;
        }),
      );
      this.addDependency(
        await tauri.listen("app:foreground", () => {
          this.state.appInBackground = false;
          void this.checkForUpdates();
        }),
      );
    }

    this.state.showAgent = sessionStorage.getItem("showAgent") === "true";

    this.registerAgentResources();
    this.observeFeedbackMessages();
  }

  async observeFeedbackMessages() {
    if (!this.sessionController.signedIn) {
      this.state.feedbackMessagesLoading = false;
      return;
    }

    this.state.feedbackMessagesLoading = true;
    try {
      const query = FeedbackMessage.query().limit(50).orderBy("created_at", "asc").preload("states");
      const messages = await query.all();
      this.state.feedbackMessages = new List(...messages);

      // Check for new messages initially
      this.updateNewFeedbackMessages();

      // Subscribe to changes
      this.addDependency(await subscribeList(this.state.feedbackMessages, query));

      // Observe the list for changes
      this.observe(() => {
        this.updateNewFeedbackMessages();
      });
    } finally {
      this.state.feedbackMessagesLoading = false;
    }
  }

  updateNewFeedbackMessages() {
    this.state.newFeedbackMessages = this.state.feedbackMessages.some((message) => {
      return !message.from_user && message.states.every((state) => state.viewed_at === null);
    });
  }

  override destroy() {
    this.clearDebugFakeTimer();
  }

  // Controller registration for tour steps
  controllers: Record<string, any> = {};
  registerController(name: string, controller: any) {
    this.controllers[name] = controller;
  }
  unregisterController(name: string) {
    delete this.controllers[name];
  }

  get sessionController() {
    return this.get(SessionController);
  }

  get routeController() {
    return this.get(RouteController);
  }

  get isOwner() {
    return Account.current?.user_id === this.sessionController.user?.id;
  }

  async checkForUpdates() {
    if (!tauri.enabled) return;
    if (this.state.appInBackground) return;
    if (!shouldCheckForUpdates() && !isDebugUpdateUiEnabled()) return;

    await confirmHotswapReady();

    if (isDebugUpdateUiEnabled()) {
      await this.maybeStartAutoDownload();
      return;
    }

    if (!this.state.online) return;

    const hotswap = await tryHotswapFrontendUpdate();
    if (hotswap.kind === "applied") {
      return;
    }

    try {
      const channel = await resolveUpdateChannel();
      const updates = await tauri.invoke<{ shouldUpdate: boolean; manifest?: { version?: string } }>(
        "check_for_updates",
        { channel },
      );
      if (updates) {
        if (!updates.shouldUpdate) {
          this.state.updateAvailable = false;
          this.state.updateVersion = undefined;
          this.resetClientUpdateFlow();
          return;
        }
        this.state.updateAvailable = true;
        this.state.updateVersion = updates.manifest?.version;
        await this.maybeStartAutoDownload();
      }
    } catch (err) {
      console.error(err);
      captureException(err, { extra: { context: "AppController.checkForUpdates" } });
    }
  }

  private resetClientUpdateFlow() {
    this.clearDebugFakeTimer();
    this.state.updatePhase = "idle";
    this.state.updateDownloadProgress = 0;
    this.state.updateError = undefined;
    this.state.updateCached = undefined;
    this.state.updateDevMock = undefined;
  }

  private clearDebugFakeTimer() {
    if (this.debugFakeTimer) {
      clearInterval(this.debugFakeTimer);
      this.debugFakeTimer = undefined;
    }
  }

  private startDebugFakeDownload() {
    this.clearDebugFakeTimer();
    this.state.updateAvailable = true;
    this.state.updateVersion = "dev-ui";
    this.state.updatePhase = "downloading";
    this.state.updateDownloadProgress = 0;
    this.state.updateError = undefined;
    this.state.updateCached = true;
    this.state.updateDevMock = true;
    let p = 0;
    this.debugFakeTimer = setInterval(() => {
      p += 15;
      this.state.updateDownloadProgress = Math.min(100, p);
      if (p >= 100) {
        this.clearDebugFakeTimer();
        this.state.updatePhase = "ready";
        this.state.updateCached = true;
      }
    }, 180);
  }

  async maybeStartAutoDownload() {
    if (!tauri.enabled) return;
    if (!shouldCheckForUpdates() && !isDebugUpdateUiEnabled()) return;

    if (isDebugUpdateUiEnabled()) {
      this.startDebugFakeDownload();
      return;
    }

    if (!this.state.online || !this.state.updateAvailable) return;

    if (
      this.state.updatePhase === "downloading" ||
      this.state.updatePhase === "ready" ||
      this.state.updatePhase === "installing"
    ) {
      return;
    }
    if (this.state.updatePhase === "error") return;

    this.state.updatePhase = "downloading";
    this.state.updateDownloadProgress = 0;
    this.state.updateError = undefined;
    this.state.updateCached = undefined;

    try {
      const channel = await resolveUpdateChannel();
      const manifest = await tauri.checkUpdate(channel);

      if (!manifest.shouldUpdate) {
        this.resetClientUpdateFlow();
        return;
      }

      await this.runRustDownloadForLater(channel);

      this.state.updatePhase = "ready";
      this.state.updateCached = true;
      this.state.updateDownloadProgress = 100;
    } catch (err) {
      console.error(err);
      captureException(err, { extra: { context: "AppController.maybeStartAutoDownload" } });
      this.state.updatePhase = "error";
      this.state.updateError = err instanceof Error ? err.message : String(err);
    }
  }

  private async runRustDownloadForLater(channel: "beta" | "release") {
    let unlisten: (() => void) | undefined;
    try {
      unlisten = await tauri.listen<MixitoneUpdaterProgressPayload>(
        MIXITONE_UPDATER_PROGRESS_EVENT,
        (event) => {
          const payload = event.payload as MixitoneUpdaterProgressPayload;
          const total = payload.total;
          if (total !== null && total !== undefined && total > 0) {
            this.state.updateDownloadProgress = Math.min(100, Math.round((payload.downloaded / total) * 100));
          }
        },
      );

      await tauri.downloadUpdateForLater(channel);
    } finally {
      unlisten?.();
    }
  }

  async actionInstallCachedUpdateOnStartup() {
    this.state.updatePhase = "installing";
    try {
      const installed = await tauri.installCachedUpdate();
      if (installed) {
        await relaunch();
      } else {
        this.resetClientUpdateFlow();
      }
    } catch (err) {
      console.error(err);
      captureException(err, {
        extra: { context: "AppController.actionInstallCachedUpdateOnStartup" },
      });
      this.state.updatePhase = "error";
      this.state.updateError = err instanceof Error ? err.message : String(err);
    }
  }

  async actionInstallAndRestart() {
    if (this.state.updatePhase !== "ready") return;

    if (this.state.updateDevMock) {
      this.resetClientUpdateFlow();
      this.state.updateAvailable = false;
      this.state.updateVersion = undefined;
      return;
    }

    this.state.updatePhase = "installing";
    try {
      if (this.state.updateCached) {
        const installed = await tauri.installCachedUpdate();
        if (installed) {
          await relaunch();
        }
        return;
      }
    } catch (err) {
      console.error(err);
      captureException(err, { extra: { context: "AppController.actionInstallAndRestart" } });
      this.state.updatePhase = "error";
      this.state.updateError = err instanceof Error ? err.message : String(err);
    }
  }

  async actionRetryAutoDownload() {
    this.state.updateError = undefined;
    this.state.updatePhase = "idle";
    await this.maybeStartAutoDownload();
  }

  async actionCheckForUpdates() {
    if (import.meta.env.DEV) {
      this.startDebugFakeDownload();
      return;
    }

    await this.checkForUpdates();
  }

  _loadingForUserId?: string;
  observeLoginState() {
    this.observe(() => {
      if (this.sessionController.signedIn || this.sessionController.recoveringPassword) {
        if (AccountUser.current?.user_id !== this.sessionController.user?.id) {
          if (this._loadingForUserId === this.sessionController.user?.id) return;
          this._loadingForUserId = this.sessionController.user?.id;

          this.loadAccount().then(() => {
            this._loadingForUserId = undefined;
          });
          return;
        }
      } else {
        // This is hit if the sign out happens externally
        this.resetAccountState();
        if (this.sessionController.signedOut) {
          this.actionForward("/login");
        }
      }
    });
  }

  async actionSignOut() {
    await this.sessionController.actionSignOut();
  }

  /**
   * Reset important state to null when the user changes or signs out
   */
  resetAccountState() {
    this.state.accounts = new List(...[]);
    this.state.clubs = new List();
    this.state.currentClub = null;

    AccountUser.current = null;
    void oom.setAccountId(null);
  }

  async loadAccount() {
    const user = this.sessionController.user;
    if (user?.id === AccountUser.current?.user_id) return;

    this.state.loading = true;
    this.resetAccountState();
    if (!user?.id) {
      this.state.loading = false;
      return;
    }

    try {
      const accountUsers = await AccountUser.forUser(user.id);
      if (accountUsers.length === 0 && user.is_anonymous) {
        throw new Error("No account users found for anonymous user");
      }
      if (accountUsers.length === 0) {
        await this.createAccount();
      } else {
        await this.setCurrentAccountUser(accountUsers[0]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      this.state.loading = false;
    }
  }

  async createAccountUser(user: User) {
    let account_id: string;
    const adapter = this.sessionController.adapter;

    const { error, data } = await adapter.rpc<{ id: string }>("insert_trial_account", {
      uid: user.id,
    });
    if (error && error.message === "User already has an account") {
      const account = await Account.query().eq("user_id", user.id).first();
      if (!account) throw new Error("User already has an account but could not find it");
      account_id = account.id!;
    } else if (error) {
      if (error) throw error;
    } else if (!data.id) {
      throw new Error("No account id returned from insert_trial_account");
    } else {
      account_id = String(data.id);
    }

    const accountUsers = await AccountUser.forUser(user.id);
    const accountUser = accountUsers.find((au) => au.account_id === String(account_id));
    if (!accountUser) throw new Error("Could not find account user for newly created account");

    if (isNil(accountUser.name)) {
      accountUser.name = user.user_metadata?.name || "Admin";
      await accountUser.save();
    }

    return accountUser;
  }

  async createAccount() {
    const user = this.sessionController.user;
    if (!user) return;

    this.state.creatingAccount = true;

    try {
      const accountUser = await this.createAccountUser(user);
      await this.setCurrentAccountUser(accountUser);
    } catch (err) {
      console.error(err);
      await this.actionSignOut();
    } finally {
      this.state.creatingAccount = false;
    }
  }

  async setCurrentAccountUser(accountUser: AccountUser) {
    identifyUser(accountUser?.id);

    AccountUser.current = accountUser;
    if (isNil(accountUser) || isNil(accountUser.account?.id)) return;

    const account = Account.current;
    if (isNil(account)) return;

    if (tauri.enabled) {
      try {
        await createCrossSupabase();
      } catch (error) {
        console.error("Failed to sync Supabase auth before account broadcast subscribe", error);
        captureException(error, {
          extra: { context: "AppController.setCurrentAccountUser.createCrossSupabase" },
        });
      }
    }

    await oom.setAccountId(account.id ?? null);

    await account.loadFeatures();

    this.addDependency(
      "subscribeAccount",
      await Account.query()
        .eq("id", account.id)
        .subscribe({
          callback: async () => {
            await account.reload();
            await account.loadFeatures();
          },
          source: ["client", "sync"],
        }),
    );
    this.addDependency(
      "subscribeFeatures",
      await SubscriptionManager.instance.add("account_features", {
        source: "sync",
        callback: () => account.loadFeatures(),
      }),
    );

    this.resolveDependency("loadClubs");
    await Promise.all([account.clubs.load(), accountUser.loadClubPermissions()]);

    // Listen for changes to the account user's permissions and reload
    this.addDependency(
      "loadClubPermissions",
      await ClubPermission.query()
        .eq("user_id", accountUser.user_id)
        .subscribe({
          callback: () => {
            accountUser.loadClubPermissions();
          },
        }),
    );

    this.state.clubs = new List(...account.clubs);
    this.state.clubs.orderBy("name");
    this.addDependency(
      "loadClubs",
      await subscribeList(this.state.clubs, account.clubs.query, {
        callback: ({ type }) => {
          // Clubs can arrive after account load (e.g. Tauri sync). Pick one so the
          // header switcher is not stuck hidden with currentClub still null.
          if (type === "INSERT" && !this.state.currentClub) {
            void this.selectInitialCurrentClub();
          }
        },
      }),
    );

    await this.selectInitialCurrentClub();

    // Full sync may have finished without INSERT events (or finished before subscribe).
    // Re-read SQLite so we are not stuck on settings with an empty header switcher.
    if (tauri.enabled && this.state.clubs.length === 0) {
      await this.refreshClubsFromLocalDb();
    }
  }

  /**
   * Re-load account clubs from local SQLite into state.clubs.
   * Needed after Tauri full_sync, which writes rows with trigger_updates=false
   * (no mixitonedb:change / subscribeList INSERT).
   */
  async refreshClubsFromLocalDb() {
    const account = Account.current;
    if (!account?.id) return;

    try {
      await account.clubs.reload();
    } catch (error) {
      console.error("Failed to refresh clubs from local db after sync", error);
      return;
    }

    let added = false;
    for (const club of account.clubs) {
      if (!club.id || this.state.clubs.findById(club.id)) continue;
      this.state.clubs.insert(club);
      added = true;
    }

    if (added) {
      this.state.clubs.orderBy("name");
    }

    if (!this.state.currentClub) {
      await this.selectInitialCurrentClub();
    }
  }

  /**
   * Choose currentClub from localStorage or the first account club when unset.
   * Safe to call again when clubs arrive asynchronously after account load.
   */
  async selectInitialCurrentClub() {
    if (this.state.currentClub) return;

    if (this.state.clubs.length === 0 && Account.current?.id) {
      try {
        await Account.current.clubs.reload();
        for (const club of Account.current.clubs) {
          if (!club.id || this.state.clubs.findById(club.id)) continue;
          this.state.clubs.insert(club);
        }
        if (this.state.clubs.length > 0) {
          this.state.clubs.orderBy("name");
        }
      } catch (error) {
        this.debug("Error reloading clubs for initial selection", error);
      }
    }

    const currentClub = Maybe(window.localStorage.getItem("currentClub"))
      .tryMap<string, Error>(
        (value) => JSON.parse(value),
        (err) => {
          this.debug("Error parsing current club", err);
        },
      )
      .map((clubId) => this.state.clubs.findById(clubId))
      .getOrElse(() => this.state.clubs[0]);

    if (currentClub) {
      await this.actionSetCurrentClub(currentClub, false);
    }
  }

  async actionSetCurrentClubById(clubId: string) {
    let club = this.state.clubs.findById(clubId);
    if (!club) {
      try {
        club = await Club.query().find(clubId);
      } catch {
        console.warn(`Club with id ${clubId} not found`);
        return;
      }
      if (club.account_id === Account.current?.id && !this.state.clubs.findById(club.id!)) {
        this.state.clubs.insert(club);
      }
    }
    this.actionSetCurrentClub(club, false);
  }

  async actionSetCurrentClub(club: Club | null, navigate = true) {
    const previousClubId = this.state.currentClub?.id;
    this.state.currentClub = club;

    if (isNil(club)) {
      window.localStorage.removeItem("currentClub");
      return;
    }

    localStorage.setItem("currentClub", JSON.stringify(club.id));

    if (!navigate) {
      return;
    }

    // Re-selecting the same club (for example after a local club list refresh)
    // must not yank the user off account settings or other non-club pages.
    if (previousClubId === club.id) {
      return;
    }

    const pathname = this.get(RouteController).state.location?.pathname ?? window.location.pathname;
    if (clubSwitchTargetPathname(pathname, club.id!) === pathname) {
      return;
    }

    const destination = resolveClubSwitchNavigation(pathname, club.id!);
    if (destination.kind === "none") {
      return;
    }

    this.actionForward(destination.path, {
      params: destination.params,
      viewTransition: true,
    });
  }

  actionOpenSettings() {
    if (this.state.currentClub?.id) {
      this.actionForward("/settings/clubs/:clubId", {
        params: { clubId: this.state.currentClub.id },
      });
      return;
    }
    this.actionForward("/settings/account");
  }

  async actionReturnOrHome() {
    if (Account.current) {
      if (this.state.currentClub) {
        this.debug("forward to current club");
        this.actionForward("/clubs/:clubId", { params: { clubId: this.state.currentClub.id! } });
        return;
      }
      if (this.state.clubs[0]) {
        this.debug("forward to first club");
        this.actionSetCurrentClub(this.state.clubs[0]);
        return;
      }
      this.debug("forward to account");
      this.actionForward("/settings/account");
      return;
    }

    this.debug("no account, forward to /not_found");
    this.actionForward("/not_found");
  }

  observeTitle() {
    this.observe(() => {
      const clubName = this.state.currentClub?.name ?? "";
      const segments = [...this.titles.filter((t) => t && t.length > 0), clubName].filter(
        (t) => t.length > 0,
      );
      document.title = segments.length > 0 ? ["MixItOne", ...segments].join(" - ") : "MixItOne";
    });
  }

  actionAppendTitle(title: string) {
    this.titles.push(title);
    return this.titles.length - 1;
  }

  actionRemoveTitle(title: string, index: number) {
    if (this.titles[index] === title) {
      if (index === this.titles.length - 1) {
        this.titles.splice(index, 1);
      } else {
        this.titles[index] = "";
      }
    }
  }

  headerElements: Set<HTMLElement> = new Set();
  updateHeaderHeight() {
    this.state.headerHeight = [...this.headerElements].reduce((acc, el) => acc + el.offsetHeight, 0);
  }

  actionAddHeaderElement(element: HTMLElement) {
    this.headerElements.add(element);
    this.updateHeaderHeight();
  }
  actionRemoveHeaderElement(element: HTMLElement) {
    this.headerElements.delete(element);
    this.updateHeaderHeight();
  }

  actionShowAgent(show: boolean) {
    this.state.showAgent = show;
    sessionStorage.setItem("showAgent", show.toString());
  }

  /**
   * Open the assistant popup with a domain-specific prompt from feature shortcuts (e.g. "Ask AI
   * to build this page"). If the popup is already open, starts a new chat and pre-fills the
   * input; otherwise pre-fills on open. The user always sends manually.
   */
  actionShowAssistantWithPrefill(text: string) {
    const mode = this.state.showAgent ? "new-chat" : "prefill";
    if (this.assistantShortcutHandler) {
      this.assistantShortcutHandler(text, mode);
    } else if (mode === "new-chat") {
      this.state.pendingAssistantNewChatPrefill = text;
    } else {
      this.state.pendingAssistantPrefill = text;
    }
    this.actionShowAgent(true);
  }

  /**
   * Read-and-clear the pending prefill. Called by `AgentController.initialize()`.
   */
  consumePendingAssistantPrefill(): string | null {
    const text = this.state.pendingAssistantPrefill ?? null;
    if (text != null) this.state.pendingAssistantPrefill = null;
    return text;
  }

  /**
   * Read-and-clear a pending new-chat prefill. Called by `AgentController.initialize()`.
   */
  consumePendingAssistantNewChatPrefill(): string | null {
    const text = this.state.pendingAssistantNewChatPrefill ?? null;
    if (text != null) this.state.pendingAssistantNewChatPrefill = null;
    return text;
  }

  consumePendingAssistantLaunch(): { message: string; additionalContext?: string } | null {
    const launch = this.state.pendingAssistantLaunch ?? null;
    if (launch != null) this.state.pendingAssistantLaunch = null;
    return launch;
  }

  /**
   * Open the assistant, start a new chat, and send a message (with optional hidden context).
   * Works when the popup is closed — uses pending state until AgentController mounts.
   */
  actionLaunchAssistantChat(message: string, additionalContext?: string) {
    if (this.assistantLaunchHandler) {
      this.assistantLaunchHandler(message, additionalContext);
    } else {
      this.state.pendingAssistantLaunch = { message, additionalContext };
    }
    this.actionShowAgent(true);
  }

  actionShowFeedback(show: boolean) {
    this.state.showFeedback = show;
  }

  /**
   * Register agent resources and tools for AI agent access
   */
  private registerAgentResources() {
    this.addDependency(
      agentToolsService.addResources(this, {
        context: () => {
          const account = Account.current;
          const hasTournamentsFeature = account?.hasFeature("tournaments") ?? false;
          const isPro = account?.isPro ?? false;

          const contextParts: string[] = [];

          if (this.state.currentClub) {
            contextParts.push(
              `Current club is ${this.state.currentClub.name} (${this.state.currentClub.id})`,
            );
          }

          contextParts.push(`Account plan: ${account?.plan ?? "Unknown"}.`);

          if (hasTournamentsFeature) {
            contextParts.push(`The account has access to tournaments (Pro feature enabled).`);
          } else {
            contextParts.push(
              `The account does NOT have access to tournaments. ${isPro ? "The account is Pro but tournaments feature is not enabled." : "Tournaments require a Pro account. The user needs to upgrade to Pro to access tournaments."}`,
            );
          }

          return contextParts.join(" ");
        },
        definitions: {
          clubs: resource("clubs", Club, {
            list: () => this.state.clubs,
          }),
        },
        toolGroups: {
          ...registerBrowserTools(),
          ...registerResourceAnalysisTools(),
        },
        tools: {
          change_current_club: {
            name: "change_current_club",
            description:
              "Change the current club to a different club. The user will be navigated to the new club's page. Provide either clubId or clubName to identify the club.",
            parameters: {
              clubId: "string (optional - the ID of the club to switch to)",
              clubName:
                "string (optional - the name of the club to switch to, case-insensitive partial match)",
            },
            execute: async (args: { clubId?: string; clubName?: string }) => {
              if (!args.clubId && !args.clubName) {
                throw new Error("Either clubId or clubName must be provided");
              }

              let club: Club | undefined;

              if (args.clubId) {
                club = this.state.clubs.findById(args.clubId);
                if (!club) {
                  throw new Error(`Club with ID "${args.clubId}" not found`);
                }
              } else if (args.clubName) {
                const searchName = args.clubName.toLowerCase().trim();
                club = this.state.clubs.find((c) => c.name?.toLowerCase().includes(searchName));
                if (!club) {
                  const availableClubs = this.state.clubs.map((c) => c.name).join(", ");
                  throw new Error(
                    `Club with name matching "${args.clubName}" not found. Available clubs: ${availableClubs || "none"}`,
                  );
                }
              }

              if (club) {
                await this.actionSetCurrentClub(club, true);
                return {
                  success: true,
                  message: `Successfully changed current club to "${club.name}" (${club.id})`,
                };
              }

              throw new Error("Failed to change club");
            },
          },
        },
      }),
    );
  }
}
